import React from "react";
import { ArrowRight } from "lucide-react";

import { fetchLoginUrl } from "../api";
import { Button } from "./ui/button";

// Track the active theme straight off the `data-theme` attribute App sets on
// <html>, so the backdrop art swaps with the theme even though LoginScreen is
// rendered outside the app shell and gets no `dark` prop. We read the attribute
// (not localStorage) so it stays in step if the theme changes while this screen
// is up. The image is referenced inline rather than via a Tailwind arbitrary
// background-image utility on purpose: css-loader tries to module-resolve URLs
// baked into CSS and can't find files served from /public, so only the inline
// style path works for public assets. (The arbitrary utility is also avoided
// in this very comment so Tailwind's JIT scanner doesn't generate a broken
// rule from the comment text.)
function useIsDark() {
  const [dark, setDark] = React.useState(
    () => document.documentElement.getAttribute("data-theme") === "dark"
  );
  React.useEffect(() => {
    const el = document.documentElement;
    const sync = () =>
      setDark(el.getAttribute("data-theme") === "dark");
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

export default function LoginScreen() {
  const dark = useIsDark();
  const handleLogin = () => {
    fetchLoginUrl()
      .then((url) => {
        window.location.href = url;
      })
      .catch(() => {
        window.location.href = "/api/auth/login-url";
      });
  };

  return (
    // Full-viewport World-Travel artwork (globe, flight paths, green/amber
    // waves, landmarks) with a clean sign-in card floated over it. The image
    // already carries all the colour, so the surface stays neutral. A separate
    // dark-mode artwork is swapped in via the `dark:` background-image variant
    // (Tailwind's dark variant is bound to `[data-theme="dark"]`), so the
    // backdrop stays legible behind the dark card. `overflow-y-auto` +
    // items-start-from-sm keeps the card reachable on a short/landscape phone
    // where it would otherwise clip off the top.
    <div
      className="flex min-h-dvh w-full items-center justify-center overflow-y-auto bg-cover bg-center bg-no-repeat px-4 py-8 bg-white dark:bg-neutral-950"
      style={{
        backgroundImage: `url('${
          dark ? "/Dark-Mode-World-Travel.png" : "/World-Travel.png"
        }')`
      }}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-black/5 bg-white/95 p-6 text-center backdrop-blur-sm dark:border-white/10 dark:bg-neutral-900/90 sm:max-w-md sm:p-10"
        // A soft, diffuse lift off the artwork — Tailwind's shadow-2xl uses a
        // near-black/10 that barely reads on the pale light background, so a
        // custom two-layer shadow (a tight ambient one + a large soft one)
        // gives the clear floating-card look from the reference design. The
        // `--login-card-shadow` CSS var lets the dark theme deepen it against
        // the darker artwork (see index.css).
        style={{
          boxShadow: "var(--login-card-shadow)"
        }}
      >
        <img
          src="/asmara-logo.png"
          alt="ASMARA"
          className="mx-auto mb-4 h-14 w-14 object-contain sm:mb-5 sm:h-16 sm:w-16"
        />

        <h1 className="text-lg font-bold leading-tight tracking-tight text-neutral-900 dark:text-neutral-50 sm:text-xl">
          ASMARA Expense Reimbursement
        </h1>
        <p className="mx-auto mt-2 max-w-xs text-sm text-neutral-500 dark:text-neutral-400">
          Sign in to manage travel requests, approvals, and policies.
        </p>

        {/* MS glyph + label are grouped and centred; the green circular arrow
            badge is pushed to the far right edge (ml-auto) — matching the
            reference design. h-auto overrides the shadcn Button's fixed height
            so the taller pill renders correctly. */}
        <Button
          type="button"
          variant="outline"
          onClick={handleLogin}
          className="group mt-6 flex h-auto w-full items-center rounded-xl border-neutral-200 bg-white px-3 py-2.5 text-sm font-semibold text-neutral-900 shadow-sm transition-colors hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:border-white/15 dark:bg-neutral-800 dark:text-neutral-50 dark:shadow-none dark:hover:bg-neutral-700 dark:focus-visible:ring-offset-neutral-900 sm:mt-7 sm:px-4 sm:py-3 sm:text-base"
        >
          <span className="flex flex-1 items-center justify-center gap-3">
            <svg
              width="20"
              height="20"
              viewBox="0 0 21 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
              className="shrink-0"
            >
              <path d="M10 1H1v9h9V1z" fill="#f25022" />
              <path d="M20 1h-9v9h9V1z" fill="#7fba00" />
              <path d="M10 11H1v9h9v-9z" fill="#00a4ef" />
              <path d="M20 11h-9v9h9v-9z" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </span>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform group-hover:translate-x-0.5">
            <ArrowRight className="h-4 w-4" />
          </span>
        </Button>
      </div>
    </div>
  );
}
